import { countBy, maxBy } from 'es-toolkit';

import { PlateGeometryType, PlatePair, PlateType } from '@paragon/contract-library';
import { assertExhaustiveSwitch } from '@paragon/utilities';

import { isComboPlateType } from './comboPlates';

const commonPlateTypes: readonly PlateType[] = [
  'M20',
  'Eagle20',
  'TL20',
  'MT20',
  'AS20',
  'AlpineW',
  'CH20',
  'CherokeeB',
  'Any20',
];

export function getMostCommonPlateType(
  platePairs: readonly PlatePair[],
): Pick<PlatePair, 'plateType' | 'tangiblePlateTypesForComboPlateType'> | undefined {
  const grouped = new Map<string, PlatePair[]>();

  for (const platePair of platePairs) {
    const key = getPlateTypeString(platePair.plateType, platePair.tangiblePlateTypesForComboPlateType);
    if (!grouped.has(key)) {
      grouped.set(key, []);
    }
    grouped.get(key)!.push(platePair);
  }

  let mostCommonPlatePair:
    (Pick<PlatePair, 'plateType' | 'tangiblePlateTypesForComboPlateType'> & { count: number }) | undefined;

  for (const group of grouped.values()) {
    const plateType = group[0].plateType;
    const tangiblePlateTypesForComboPlateType = group[0].tangiblePlateTypesForComboPlateType;

    const isCommon =
      commonPlateTypes.includes(plateType) ||
      tangiblePlateTypesForComboPlateType.every((tangiblePlateType) => commonPlateTypes.includes(tangiblePlateType));

    if (isCommon && (!mostCommonPlatePair || group.length > mostCommonPlatePair.count)) {
      mostCommonPlatePair = {
        plateType,
        tangiblePlateTypesForComboPlateType,
        count: group.length,
      };
    }
  }

  return mostCommonPlatePair
    ? {
        plateType: mostCommonPlatePair.plateType,
        tangiblePlateTypesForComboPlateType: mostCommonPlatePair.tangiblePlateTypesForComboPlateType,
      }
    : undefined;
}

export function getMostCommonPlateSize(
  platePairs: readonly PlatePair[],
): Pick<PlatePair, 'width' | 'length' | 'geometryType'> | undefined {
  const plateSizeCounts = countBy(platePairs, (platePair) => getPlateSizeString(platePair, true));
  const mostCommonSizeString = maxBy(Object.keys(plateSizeCounts), (plateSize) => plateSizeCounts[plateSize]);
  if (!mostCommonSizeString) return undefined;

  const [width, length] = mostCommonSizeString
    .replace(tPlatePrefix, '')
    .replace(bevelPlatePrefix, '')
    .replace(bevelPlateLeftHandedSuffix, '')
    .replace(bevelPlateRightHandedSuffix, '')
    .split(by)
    .map(parseFloat);

  let geometryType: PlateGeometryType = 'Rectangular';
  if (mostCommonSizeString.startsWith(tPlatePrefix)) {
    geometryType = 'TPlate';
  } else if (mostCommonSizeString.startsWith(bevelPlatePrefix)) {
    geometryType = mostCommonSizeString.endsWith(bevelPlateRightHandedSuffix)
      ? 'BevelPlateRightHanded'
      : 'BevelPlateLeftHanded';
  }

  return { length, width, geometryType };
}

/**
 * Corresponds to server-side method `GetPlateTypeString` in PlatePrototype.cs
 */
export function getPlateTypeString(plateType: PlateType, tangiblePlateTypes: readonly PlateType[]): string {
  if (!isComboPlateType(plateType)) {
    return plateType;
  }
  return tangiblePlateTypes.toSorted().join('/');
}

export const by = 'x';
export const tPlatePrefix = 'T';
export const bevelPlatePrefix = 'BV';
export const bevelPlateRightHandedSuffix = 'RH';
export const bevelPlateLeftHandedSuffix = 'LH';

/**
 * Corresponds to server-side method `GetPlateSizePrefixForGeometryType` in PlatePrototype.cs
 */
function getPlateSizePrefixForGeometryType(geometryType: PlateGeometryType): string {
  switch (geometryType) {
    case 'Rectangular':
    case 'Hinged':
      return '';
    case 'TPlate':
      return tPlatePrefix;
    case 'BevelPlateLeftHanded':
    case 'BevelPlateRightHanded':
      return bevelPlatePrefix;
    default:
      assertExhaustiveSwitch(geometryType);
  }
}

/**
 * Generally, the left-/right-handedness of a bevel plate does not matter, since one of each is used in a plate pair. In
 * some cases, though, such as geometry cache keying, we need to distinguish between them.
 *
 * Corresponds to server-side method `GetPlateSizeSuffixForGeometryType` in PlatePrototype.cs
 */
function getPlateSizeSuffixForGeometryType(geometryType: PlateGeometryType): string {
  switch (geometryType) {
    case 'Rectangular':
    case 'TPlate':
    case 'Hinged':
      return '';
    case 'BevelPlateLeftHanded':
      return bevelPlateLeftHandedSuffix;
    case 'BevelPlateRightHanded':
      return bevelPlateRightHandedSuffix;
    default:
      assertExhaustiveSwitch(geometryType);
  }
}

/**
 * Corresponds to server-side method `GetPlateSizeString` in PlatePrototype.cs, except that dimensions are rounded there
 */
export function getPlateSizeString(
  platePair: Pick<PlatePair, 'geometryType' | 'width' | 'length'>,
  includeSuffixForGeometryType = false,
): string {
  return `${getPlateSizePrefixForGeometryType(platePair.geometryType)}${platePair.width}${by}${platePair.length}${includeSuffixForGeometryType ? getPlateSizeSuffixForGeometryType(platePair.geometryType) : ''}`;
}

/**
 * Corresponds to server-side method `GetPlateTypeAndSizeString` in PlatePrototype.cs, except that dimensions are
 * rounded there
 */
export function getPlateTypeAndSizeString(
  platePair: Pick<PlatePair, 'plateType' | 'tangiblePlateTypesForComboPlateType' | 'geometryType' | 'width' | 'length'>,
  includeSuffixForGeometryType = false,
) {
  return `${getPlateTypeString(platePair.plateType, platePair.tangiblePlateTypesForComboPlateType)} ${getPlateSizeString(platePair, includeSuffixForGeometryType)}`;
}

export function comparePlateGeometryType(geometryType: PlateGeometryType): number {
  switch (geometryType) {
    case 'Rectangular':
      return 0;
    case 'TPlate':
      return 1;
    case 'BevelPlateLeftHanded':
      return 2;
    case 'BevelPlateRightHanded':
      return 3;
    case 'Hinged':
      return 4;
    default:
      assertExhaustiveSwitch(geometryType);
  }
}

export function isPlateAnalysisSupported(platePair: PlatePair) {
  return platePair.geometryType !== 'Hinged';
}
